import { useState } from "react";
import { Icon } from "@juiceforward/narrative-player";
import { useAdminTheme } from "../../contexts/useAdminTheme";
import { AdminTheme } from "../../contexts/AdminThemeContext";

export const SettingsView = () => {
  const { theme, setTheme } = useAdminTheme();
  const [settings, setSettings] = useState({
    companyName: "Teaching Strategies",
    supportEmail: "support@teachingstrategies.com",
    allowPublicSharing: true,
    requireAuthentication: true,
    enableAnalytics: true,
    autoArchiveDays: "90",
  });

  const handleInputChange = (key: string, value: string | boolean) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const themeOptions: { value: AdminTheme; label: string; icon: string }[] = [
    { value: "light", label: "Light", icon: "sun" },
    { value: "dark", label: "Dark", icon: "moon" },
    { value: "system", label: "System", icon: "desktop" },
  ];

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Appearance Settings */}
      <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
          <Icon icon="palette" className="text-emerald-400" />
          Appearance
        </h3>
        <div>
          <label className="block text-sm font-medium text-[var(--admin-text-muted)] mb-3">
            Theme
          </label>
          <div className="flex gap-3">
            {themeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setTheme(option.value)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-colors text-sm font-medium ${
                  theme === option.value
                    ? "bg-emerald-500/20 border-emerald-500 text-emerald-400"
                    : "bg-[var(--admin-input-bg)] border-[var(--admin-border-secondary)] text-[var(--admin-text-muted)] hover:border-emerald-500/50 hover:text-[var(--admin-text)]"
                }`}
              >
                <Icon icon={option.icon} />
                {option.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-[var(--admin-text-subtle)] mt-2">
            System follows your device's color scheme preference.
          </p>
        </div>
      </div>

      {/* General Settings */}
      <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
          <Icon icon="building" className="text-emerald-400" />
          General Settings
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--admin-text-muted)] mb-2">
              Company Name
            </label>
            <input
              type="text"
              value={settings.companyName}
              onChange={(e) => handleInputChange("companyName", e.target.value)}
              className="w-full bg-[var(--admin-input-bg)] border border-[var(--admin-border-secondary)] rounded-lg px-4 py-2.5 text-[var(--admin-text)] focus:outline-none focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--admin-text-muted)] mb-2">
              Email Contact Narrative Default
            </label>
            <input
              type="email"
              value={settings.supportEmail}
              onChange={(e) =>
                handleInputChange("supportEmail", e.target.value)
              }
              className="w-full bg-[var(--admin-input-bg)] border border-[var(--admin-border-secondary)] rounded-lg px-4 py-2.5 text-[var(--admin-text)] focus:outline-none focus:border-emerald-500"
            />
          </div>
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
          <Icon icon="shield-halved" className="text-emerald-400" />
          Security & Privacy
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <div className="font-medium">Require Authentication</div>
              <div className="text-sm text-[var(--admin-text-subtle)]">
                Users must log in to view narratives
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.requireAuthentication}
                onChange={(e) =>
                  handleInputChange("requireAuthentication", e.target.checked)
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[var(--admin-border-secondary)] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
            </label>
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <div className="font-medium">Allow Public Sharing</div>
              <div className="text-sm text-[var(--admin-text-subtle)]">
                Enable public narrative links
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.allowPublicSharing}
                onChange={(e) =>
                  handleInputChange("allowPublicSharing", e.target.checked)
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[var(--admin-border-secondary)] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
            </label>
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <div className="font-medium">Enable Analytics Tracking</div>
              <div className="text-sm text-[var(--admin-text-subtle)]">
                Collect usage statistics and metrics
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enableAnalytics}
                onChange={(e) =>
                  handleInputChange("enableAnalytics", e.target.checked)
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[var(--admin-border-secondary)] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
          <Icon icon="database" className="text-emerald-400" />
          Data Management
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--admin-text-muted)] mb-2">
              Auto-Archive After (days)
            </label>
            <input
              type="number"
              value={settings.autoArchiveDays}
              onChange={(e) =>
                handleInputChange("autoArchiveDays", e.target.value)
              }
              className="w-full bg-[var(--admin-input-bg)] border border-[var(--admin-border-secondary)] rounded-lg px-4 py-2.5 text-[var(--admin-text)] focus:outline-none focus:border-emerald-500"
            />
            <p className="text-sm text-[var(--admin-text-subtle)] mt-1">
              Narratives will be automatically archived after this many days of
              inactivity
            </p>
          </div>
        </div>
      </div>

      {/* API Keys (Placeholder) */}
      <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
          <Icon icon="key" className="text-emerald-400" />
          API Keys
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-[var(--admin-input-bg)] rounded-lg border border-[var(--admin-border-secondary)]">
            <div className="flex items-center gap-3 flex-1">
              <Icon icon="key" className="text-[var(--admin-text-subtle)]" />
              <div className="flex-1 min-w-0">
                <div className="font-medium font-mono text-sm">
                  fwd_••••••••••••••••
                </div>
                <div className="text-xs text-[var(--admin-text-subtle)]">
                  Created on Feb 1, 2026
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="text-[var(--admin-text-muted)] hover:text-[var(--admin-text)] transition-colors text-sm">
                <Icon icon="copy" className="mr-1" />
                Copy
              </button>
              <button className="text-red-400 hover:text-red-300 transition-colors text-sm">
                <Icon icon="trash" />
              </button>
            </div>
          </div>
          <button className="w-full px-4 py-2.5 bg-[var(--admin-input-bg)] border border-[var(--admin-border-secondary)] hover:border-emerald-500 rounded-lg transition-colors text-sm font-medium">
            <Icon icon="plus" className="mr-2" />
            Generate New API Key
          </button>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center gap-3">
        <button className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors font-medium text-white">
          <Icon icon="save" className="mr-2" />
          Save Changes
        </button>
        <button className="px-6 py-2.5 bg-[var(--admin-surface)] border border-[var(--admin-border-secondary)] hover:border-[var(--admin-text-muted)] rounded-lg transition-colors font-medium">
          Cancel
        </button>
      </div>
    </div>
  );
};
